import styled from "styled-components";
import colors from "../../constants/colors";
import { CardVariant, getVariantColors } from "../../utils/colorVariants";

export const CollapsibleWrapper = styled("div")({
  marginBottom: "0.75rem",
  width: "100%",
});

export const CollapsibleHeader = styled("div")<{
  $isCollapsed?: boolean;
  $secondary?: boolean;
  $variant?: CardVariant;
}>(({ $isCollapsed, $secondary, $variant = "default" }) => {
  const variantColors = getVariantColors($variant);

  return {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: $secondary
      ? colors.PURPLE_LIGHTER
      : variantColors.backgroundGradient,
    backdropFilter: $secondary ? "none" : "blur(12px) saturate(180%)",
    WebkitBackdropFilter: $secondary ? "none" : "blur(12px) saturate(180%)",
    border: $secondary
      ? `1px solid ${colors.GREY_6}`
      : `1px solid ${variantColors.border}`,
    borderRadius: $isCollapsed ? "10px" : "10px 10px 0 0",
    padding: $secondary ? "0.625rem 0.75rem" : "0.75rem 0.875rem",
    cursor: "pointer",
    boxShadow: $secondary
      ? "none"
      : `0px 1px 4px ${colors.SHADOW_LIGHT}, 0px 1px 2px ${colors.SHADOW_PURPLE_LIGHT}, 0 0 12px ${variantColors.accentGlow}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    minHeight: $secondary ? "44px" : "52px",

    "&::before": $secondary ? {} : {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent 0%, ${variantColors.border} 50%, transparent 100%)`,
      opacity: 0.6,
    },

    // Accessibility: Focus indicators
    "&:focus": {
      outline: `3px solid ${variantColors.border}`,
      outlineOffset: "2px",
    },

    "&:focus-visible": {
      outline: `3px solid ${variantColors.border}`,
      outlineOffset: "2px",
    },
  };
});

export const TitleWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
});

export const CollapsibleTitle = styled("span")({
  fontSize: "15px",
  fontWeight: "bold",
  color: colors.BLACK,
  flexGrow: 1,
  textAlign: "right",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.125rem",
});

export const CollapsibleCount = styled("div")({
  marginLeft: "6px",
  color: colors.GREY_7,
  fontSize: "0.875em",
});

export const CollapsibleSubtitle = styled("span")({
  fontSize: "11px",
  color: colors.GREY_7,
});

export const CollapsibleContent = styled("div")<{
  $maxHeight: number | "none";
  $isExpanded: boolean;
}>(({ $maxHeight, $isExpanded }) => ({
  maxHeight: $maxHeight === "none" ? "none" : `${$maxHeight}px`,
  opacity: $isExpanded ? 1 : 0,
  transition: "max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  padding: $isExpanded ? "0.625rem 0.75rem" : "0 0.75rem",
  backgroundColor: colors.WHITE,
  border: `1px solid ${colors.GLASS_BORDER}`,
  borderRadius: $isExpanded ? "0 0 10px 10px" : "0",
  borderTop: "none",
}));

export const HeaderIcon = styled("div")<{ $variant?: CardVariant }>(
  ({ $variant = "default" }) => {
    const variantColors = getVariantColors($variant);

    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: variantColors.iconBg,
      color: variantColors.iconColor,
      marginLeft: "0.5rem",
      flexShrink: 0,
      boxShadow: `0 2px 8px ${variantColors.accentGlow}, 0 1px 3px rgba(0, 0, 0, 0.08)`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",

      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "8px",
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)",
        pointerEvents: "none",
      },

      "& .MuiSvgIcon-root": {
        fontSize: "20px",
        position: "relative",
        zIndex: 1,
        color: "inherit",
      },

      "& svg": {
        fontSize: "20px",
        position: "relative",
        zIndex: 1,
        color: "inherit",
        fill: "currentColor",
      },
    };
  },
);

export const IconWrapper = styled("span")({
  marginLeft: "0.5rem",
  display: "flex",
  alignItems: "center",
});

export const DeleteButton = styled("button")({
  background: colors.RED,
  border: "none",
  color: colors.WHITE,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  padding: "0.25rem 0.5rem",
  fontSize: "14px",
  fontWeight: "700",
  marginLeft: "0.25rem",
  height: "100%",
});
